import React, { useEffect, useState } from 'react';
import {
    Card,
    Select,
    Button,
    Badge,
    Tabs,
    Progress,
    Divider,
    Space,
    Typography,
    Row,
    Col,
    Statistic,
    Tag,
    Modal,
    Tooltip,
    message,
    Spin,
    Switch,
} from 'antd';
import {
    WifiOutlined,
    CreditCardOutlined,
    SafetyOutlined,
    SettingOutlined,
    MobileOutlined,
    ReloadOutlined,
    DownloadOutlined,
    CopyOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ThunderboltOutlined,
    SignalFilled,
    ControlOutlined,
    PoweroffOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    ApiOutlined,
    BugOutlined,
    SoundOutlined,
    TabletOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useGetLogs } from '@/store/Totem/GetLogs';
import { useGetTotems } from '@/store/Totem/useGetTotems';
import { IGetLastLogTotem } from '@/store/Totem/types';
import { useSendReloadApp } from '@/store/Totem/useSendReload';
import { useSendLogcatCommand } from '@/store/Totem/useSendLogcatCommand';
import { useSendTotemCommand } from '@/store/Totem/useSendTotemCommand';
import { ExportLogsTotemModal } from '@/components/Modals/ExportLogsTotemModal';
import { useAuth } from '@/contexts/Auth';
import { useTheme } from '@/contexts/Theme';
import { getAuxDeviceAddressMeta } from '@/utils/auxDeviceAddress';
import { TotemProps } from '../types';

const { Title, Text } = Typography;

interface DataStoreProps {
    id?: number;
    totems?: TotemProps[];
}

interface IndicatorsProps {
    data: DataStoreProps | undefined;
    initialTotemId?: number;
    embedded?: boolean;
    embeddedTotem?: TotemProps;
    forceAdminActions?: boolean;
}

function WifiSignalBars({ level }: { level: number }) {
    const clamped = Math.min(100, Math.max(0, level));
    const filled =
        clamped <= 0 ? 0 : Math.min(4, Math.ceil((clamped / 100) * 4));
    const heights = [10, 14, 18, 22];
    const activeColor = clamped < 50 ? '#ff4d4f' : '#52c41a';

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'flex-end',
                gap: 3,
                marginRight: 4,
            }}
        >
            {heights.map((h, i) => (
                <span
                    key={i}
                    style={{
                        width: 5,
                        height: h,
                        borderRadius: 2,
                        backgroundColor:
                            i < filled ? activeColor : 'rgba(0,0,0,0.08)',
                        transition: 'background-color 0.2s ease',
                    }}
                />
            ))}
        </span>
    );
}

export default function TotemDashboard({
    data: storeData,
    initialTotemId,
    embedded,
    embeddedTotem,
    forceAdminActions,
}: IndicatorsProps) {
    const { isAssuming } = useAuth();
    const showAdminActions = isAssuming || !!forceAdminActions;
    const [selectedTotem, setSelectedTotem] = useState<number | null>(() =>
        embedded ? embeddedTotem?.id ?? initialTotemId ?? null : null,
    );
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IGetLastLogTotem | undefined>(undefined);
    const [totemsFromApi, setTotemsFromApi] = useState<TotemProps[]>([]);
    const [openModalExportLogs, setOpenModalExportLogs] = useState(false);
    const [logcatModalOpen, setLogcatModalOpen] = useState(false);
    const [logcatDownloadUrl, setLogcatDownloadUrl] = useState<string | null>(
        null,
    );
    const [pendingToggle, setPendingToggle] = useState<
        'kiosk' | 'audio' | null
    >(null);
    const [isKioskModeEnabled, setIsKioskModeEnabled] = useState(
        data?.has_kiosk_mode === 1,
    );
    const [isScannerAudioFeedbackEnabled, setIsScannerAudioFeedbackEnabled] =
        useState(data?.has_scanner_audio_feedback === 1);

    useEffect(() => {
        setPendingToggle(null);
    }, [selectedTotem]);

    const { theme } = useTheme();

    const { data: totemsList } = useGetTotems({
        storeId: storeData?.id,
        enabled: !embedded && !!storeData?.id,
    });

    const totems = embedded && embeddedTotem ? [embeddedTotem] : totemsFromApi;

    const {
        data: lastLogTotemSelected,
        isLoading: isLoadingLastLogTotem,
        refetch,
    } = useGetLogs({
        storeId: storeData?.id,
        totemId: selectedTotem,
    });

    const { mutateAsync: mutateAsyncSendReloadApp } = useSendReloadApp();
    const { mutateAsync: mutateAsyncSendLogcat, isLoading: isLoadingLogcat } =
        useSendLogcatCommand();
    const {
        mutateAsync: mutateAsyncSendCommand,
        isLoading: isLoadingSendCommand,
    } = useSendTotemCommand();

    useEffect(() => {
        setIsKioskModeEnabled(data?.has_kiosk_mode === 1);
        setIsScannerAudioFeedbackEnabled(
            data?.has_scanner_audio_feedback === 1,
        );
    }, [data]);

    useEffect(() => {
        setData(lastLogTotemSelected);
    }, [lastLogTotemSelected]);

    useEffect(() => {
        if (embedded) return;
        setTotemsFromApi(totemsList || []);
    }, [totemsList, embedded]);

    useEffect(() => {
        if (embedded) return;
        if (!totemsList?.length) return;
        if (!selectedTotem) {
            setSelectedTotem(totemsList[0].id);
        }
    }, [totemsList, embedded, selectedTotem]);

    const calculateUptimeHours = (uptimeDate?: string): number => {
        if (!uptimeDate) return 0;
        const now = moment();
        const uptime = moment(uptimeDate);
        const hours = now.diff(uptime, 'hours', true);
        return Math.max(0, Math.round(hours * 10) / 10);
    };

    const renderDate = (dateString?: string) => {
        return dateString
            ? moment(dateString).format('DD/MM/YYYY - HH:mm:ss')
            : 'Não informado';
    };

    const renderBool = (value?: number | boolean): string => {
        return toBool(value) ? 'Sim' : 'Não';
    };

    const renderText = (text?: string | null): string => {
        return text || 'Não informado';
    };

    const renderPercent = (value?: number): string => {
        return value !== undefined ? `${value}%` : 'Não informado';
    };

    const formatDate = (dateString?: string) => {
        return dateString
            ? moment(dateString).format('DD/MM/YYYY - HH:mm:ss')
            : 'Não informado';
    };

    const formatMemoryMb = (value?: number) => {
        return typeof value === 'number' ? `${value} MB` : 'Não informado';
    };

    const memoryUsagePercent =
        typeof data?.memory_total === 'number' &&
        typeof data?.memory_free === 'number' &&
        data.memory_total > 0
            ? Math.round(
                  ((data.memory_total - data.memory_free) / data.memory_total) *
                      100,
              )
            : undefined;

    const toBool = (value?: number | boolean): boolean => {
        return value === 1 || value === true;
    };

    const isServerOnline = (): boolean => {
        const serverAccessing = toBool(data?.is_accessing_shoppbud_server);
        const lastLogDate = data?.local_date;

        if (!lastLogDate) {
            return serverAccessing;
        }

        const now = moment();
        const logMoment = moment(lastLogDate);
        const diffMinutes = now.diff(logMoment, 'minutes');

        if (diffMinutes > 8) {
            return false;
        }

        if (!serverAccessing && diffMinutes <= 8) {
            return true;
        }

        return serverAccessing;
    };

    const getStatusColor = (status?: number | boolean): 'success' | 'error' => {
        return toBool(status) ? 'success' : 'error';
    };

    const getBatteryColor = (level?: number) => {
        if (!level) return '#d9d9d9';
        if (level > 60) return '#52c41a';
        if (level > 30) return '#faad14';
        return '#ff4d4f';
    };

    const getConnectionIcon = (connectionType?: string) => {
        const type = connectionType?.toLowerCase();
        if (type?.includes('wifi') || type?.includes('wi-fi'))
            return <WifiOutlined />;
        return <SignalFilled />;
    };

    const getTotemLabel = (totem: TotemProps) => {
        const uid = totem.android_uid || '';
        const hardware = totem.hardware_model || '';

        if (
            uid.startsWith('600') ||
            uid.startsWith('630') ||
            uid.startsWith('PB3') ||
            uid.startsWith('PBA') ||
            hardware.startsWith('600') ||
            hardware.startsWith('630') ||
            hardware.startsWith('PB3') ||
            hardware.startsWith('PBA')
        ) {
            return 'SMART';
        }
        return 'TOTEM';
    };

    const handleTotemChange = (value: number) => {
        setLoading(true);
        setSelectedTotem(value);
        setTimeout(() => {
            setLoading(false);
        }, 500);
    };

    const handleRebootTerminal = async () => {
        if (!selectedTotem) return;
        Modal.confirm({
            title: 'Reiniciar Terminal',
            icon: <ExclamationCircleOutlined />,
            content: 'Tem certeza que deseja reiniciar o terminal selecionado?',
            okText: 'Confirmar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const response = await mutateAsyncSendReloadApp({
                        totemId: Number(selectedTotem),
                    });

                    if (response?.sentCommand?.success) {
                        Modal.success({
                            content: `Comando de reinício enviado com sucesso!`,
                        });
                    } else {
                        Modal.error({
                            title: 'Erro ao enviar comando',
                            content:
                                'Não foi possível enviar o comando de reinício. Tente novamente mais tarde.',
                        });
                    }
                } catch (error) {
                    Modal.error({
                        title: 'Erro ao enviar comando',
                        content:
                            'Não foi possível enviar o comando de reinício. Tente novamente mais tarde.',
                    });
                }
            },
            onCancel: () => {
                // eslint-disable-next-line @typescript-eslint/no-empty-function
            },
        });
    };

    const handleConfirmToggle = async () => {
        if (!selectedTotem || !pendingToggle) return;

        const params =
            pendingToggle === 'kiosk'
                ? {
                      totemId: Number(selectedTotem),
                      command: 'set-config',
                      payload: {
                          config: 'kiosk_mode',
                          value: !isKioskModeEnabled,
                      },
                  }
                : {
                      totemId: Number(selectedTotem),
                      command: 'set-config',
                      payload: {
                          config: 'scanner_audio_feedback',
                          value: !isScannerAudioFeedbackEnabled,
                      },
                  };

        try {
            const response = await mutateAsyncSendCommand(params);
            if (response?.sentCommand?.success) {
                if (pendingToggle === 'kiosk') {
                    setIsKioskModeEnabled((prev) => !prev);
                    message.success(
                        `Funcionalidade ${
                            data?.has_kiosk_mode ? 'desativada' : 'ativada'
                        } com sucesso.`,
                    );
                } else {
                    setIsScannerAudioFeedbackEnabled((prev) => !prev);
                    message.success(
                        `Funcionalidade ${
                            data?.has_scanner_audio_feedback
                                ? 'desativada'
                                : 'ativada'
                        } com sucesso.`,
                    );
                }
                refetch();
            } else {
                message.error(
                    'Não foi possível conectar ao dispositivo. Verifique se a maquininha está ligada, com internet e com o app aberto.',
                );
            }
        } catch {
            message.error(
                'Erro ao enviar comando. Tente novamente mais tarde.',
            );
        } finally {
            setPendingToggle(null);
        }
    };

    const handleExportLogs = () => {
        if (!showAdminActions) {
            Modal.error({
                title: 'Ação não permitida',
                content: 'Você não tem permissão para exportar os logs.',
            });
            return;
        }
        setOpenModalExportLogs(true);
    };

    const handleCollectLogcat = () => {
        if (!showAdminActions) {
            Modal.error({
                title: 'Ação não permitida',
                content: 'Você não tem permissão para coletar o logcat.',
            });
            return;
        }
        if (!selectedTotem) return;
        if (isSmartDevice) {
            message.warning(
                'Coleta de Logcat disponível apenas para dispositivos Totem.',
            );
            return;
        }
        Modal.confirm({
            title: 'Coletar Logcat',
            icon: <BugOutlined />,
            content:
                'Deseja coletar o logcat do terminal selecionado? O processo pode levar alguns segundos.',
            okText: 'Coletar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const response = await mutateAsyncSendLogcat({
                        totemId: Number(selectedTotem),
                    });
                    const downloadUrl =
                        response?.sentCommand?.payload?.downloadUrl;

                    if (response?.sentCommand?.success && downloadUrl) {
                        message.success('Logcat coletado com sucesso!');
                        setLogcatDownloadUrl(downloadUrl);
                        setLogcatModalOpen(true);
                    } else {
                        message.error(
                            'Não foi possível coletar o logcat. Tente novamente.',
                        );
                        throw new Error('BUSINESS_ERROR');
                    }
                } catch (err) {
                    if (
                        !(err instanceof Error) ||
                        err.message !== 'BUSINESS_ERROR'
                    ) {
                        message.error(
                            'Erro ao coletar logcat. Tente novamente mais tarde.',
                        );
                    }
                    throw err;
                }
            },
            onCancel: () => {
                // eslint-disable-next-line @typescript-eslint/no-empty-function
            },
        });
    };

    const handleDownloadLogcat = () => {
        if (logcatDownloadUrl) {
            window.open(logcatDownloadUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleCloseLogcatModal = () => {
        setLogcatModalOpen(false);
        setLogcatDownloadUrl(null);
    };

    const handleRefresh = () => {
        refetch();
    };

    const handleCopyText = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success('E-mail copiado.');
        } catch {
            message.error('Não foi possível copiar o e-mail.');
        }
    };

    const selectedTotemData = totems.find((t) => t.id === selectedTotem);

    const isSmartDevice = selectedTotemData
        ? getTotemLabel(selectedTotemData) === 'SMART'
        : false;

    const auxAddressMeta = getAuxDeviceAddressMeta(
        data?.aux_mode_device_address,
    );

    const compactSectionStyle = {
        width: '100%',
        marginTop: -8,
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                background: theme === 'light' ? '#f0f2f5' : '#1f1f1f',
                padding: '16px',
            }}
        >
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%' }}
                >
                    <Card>
                        <Row
                            gutter={[16, 16]}
                            align="middle"
                            justify="space-between"
                        >
                            <Col xs={24} md={12}>
                                <Title level={2} style={{ margin: 0 }}>
                                    Status do Totem
                                </Title>
                                <Text type="secondary">
                                    Monitoramento em tempo real dos totems
                                </Text>
                            </Col>
                            <Col xs={24} md={12}>
                                <Space
                                    wrap
                                    style={{
                                        width: '100%',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {!embedded && (
                                        <Select
                                            style={{
                                                minWidth: 200,
                                                width: '100%',
                                                maxWidth: 280,
                                            }}
                                            value={selectedTotem}
                                            onChange={handleTotemChange}
                                            placeholder="Selecione um totem"
                                            loading={loading}
                                        >
                                            {totems.map((totem) => (
                                                <Select.Option
                                                    key={totem.id}
                                                    value={totem.id}
                                                >
                                                    {getTotemLabel(totem)}{' '}
                                                    {totem.android_uid}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    )}
                                    <Button
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={handleRefresh}
                                        loading={isLoadingLastLogTotem}
                                    >
                                        Atualizar
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Row gutter={[16, 16]} style={{ alignItems: 'stretch' }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card
                                style={{ height: '100%', minHeight: 130 }}
                                styles={{
                                    body: { height: '100%' },
                                }}
                            >
                                <Statistic
                                    title="Tempo de Atividade"
                                    value={calculateUptimeHours(
                                        (data as any)?.uptime,
                                    )}
                                    suffix="h"
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{
                                        color:
                                            calculateUptimeHours(
                                                (data as any)?.uptime,
                                            ) > 24
                                                ? '#3c4538'
                                                : '#faad14',
                                    }}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Desde o último reinício
                                </Text>
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card
                                style={{ height: '100%', minHeight: 130 }}
                                styles={{
                                    body: { height: '100%' },
                                }}
                            >
                                <Statistic
                                    title="Nível da Bateria"
                                    value={data?.battery_level || 0}
                                    suffix="%"
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{
                                        color: getBatteryColor(
                                            data?.battery_level,
                                        ),
                                    }}
                                />
                                <Progress
                                    percent={data?.battery_level || 0}
                                    strokeColor={getBatteryColor(
                                        data?.battery_level,
                                    )}
                                    showInfo={false}
                                    style={{ marginTop: 8 }}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card
                                style={{ height: '100%', minHeight: 130 }}
                                styles={{
                                    body: { height: '100%' },
                                }}
                            >
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Text type="secondary">Conexão</Text>
                                    <Space align="center" wrap={false}>
                                        {typeof data?.wifi_signal_percent ===
                                        'number' ? (
                                            <Tooltip
                                                title={[
                                                    `Sinal: ${data.wifi_signal_percent}%`,
                                                    data?.wifi_name &&
                                                        `Rede: ${data.wifi_name}`,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            >
                                                <span style={{ flexShrink: 0 }}>
                                                    <WifiSignalBars
                                                        level={
                                                            data.wifi_signal_percent
                                                        }
                                                    />
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <span style={{ flexShrink: 0 }}>
                                                {getConnectionIcon(
                                                    data?.connection_type,
                                                )}
                                            </span>
                                        )}
                                        <Text strong ellipsis>
                                            {!isServerOnline()
                                                ? data?.connection_type || 'N/A'
                                                : data?.wifi_name
                                                ? `${
                                                      data?.connection_type ||
                                                      'N/A'
                                                  } · ${data.wifi_name.toUpperCase()}`
                                                : data?.connection_type ||
                                                  'N/A'}
                                        </Text>
                                    </Space>
                                    <Badge
                                        status={
                                            isServerOnline()
                                                ? 'success'
                                                : 'error'
                                        }
                                        text={
                                            isServerOnline()
                                                ? 'Online'
                                                : 'Offline'
                                        }
                                    />
                                </Space>
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card
                                style={{ height: '100%', minHeight: 130 }}
                                styles={{ body: { height: '100%' } }}
                            >
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Text type="secondary">
                                        Última Atualização
                                    </Text>
                                    <Text strong style={{ fontSize: 14 }}>
                                        {formatDate(data?.local_date)}
                                    </Text>
                                </Space>
                            </Card>
                        </Col>
                    </Row>

                    <Card>
                        <Tabs
                            defaultActiveKey="pagamentos"
                            items={[
                                {
                                    key: 'pagamentos',
                                    label: (
                                        <span>
                                            <CreditCardOutlined /> &nbsp;
                                            Pagamentos
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Métodos de Pagamento"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                PIX em
                                                                funcionamento?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.pix_is_available,
                                                                )}
                                                            >
                                                                {toBool(
                                                                    data?.pix_is_available,
                                                                )
                                                                    ? 'Sim'
                                                                    : 'Não'}
                                                            </Tag>
                                                        </Row>
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Cartão de
                                                                crédito em
                                                                funcionamento?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.credit_card_is_available,
                                                                )}
                                                            >
                                                                {toBool(
                                                                    data?.credit_card_is_available,
                                                                )
                                                                    ? 'Sim'
                                                                    : 'Não'}
                                                            </Tag>
                                                        </Row>
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Cartão de débito
                                                                em
                                                                funcionamento?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.debit_card_is_available,
                                                                )}
                                                            >
                                                                {toBool(
                                                                    data?.debit_card_is_available,
                                                                )
                                                                    ? 'Sim'
                                                                    : 'Não'}
                                                            </Tag>
                                                        </Row>
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Voucher em
                                                                funcionamento?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.voucher_is_available,
                                                                )}
                                                            >
                                                                {toBool(
                                                                    data?.voucher_is_available,
                                                                )
                                                                    ? 'Sim'
                                                                    : 'Não'}
                                                            </Tag>
                                                        </Row>
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Token do
                                                                processador de
                                                                pagamentos
                                                                válido?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.plugpag_token_is_valid,
                                                                )}
                                                            >
                                                                {toBool(
                                                                    data?.plugpag_token_is_valid,
                                                                )
                                                                    ? 'Sim'
                                                                    : 'Não'}
                                                            </Tag>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Últimas Transações"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size="small"
                                                    >
                                                        <div>
                                                            <Text strong>
                                                                Última transação
                                                                de PIX com
                                                                sucesso
                                                                (Pagar.me)
                                                            </Text>
                                                            <br />
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {renderDate(
                                                                    data?.lastSalePix,
                                                                )}
                                                            </Text>
                                                        </div>
                                                        <Divider
                                                            style={{
                                                                margin: '4px 0',
                                                            }}
                                                        />
                                                        <div>
                                                            <Text strong>
                                                                Última transação
                                                                de PIX com
                                                                sucesso
                                                                (PagBank)
                                                            </Text>
                                                            <br />
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {renderDate(
                                                                    data?.lastSalePlugPagPix,
                                                                )}
                                                            </Text>
                                                        </div>
                                                        <Divider
                                                            style={{
                                                                margin: '4px 0',
                                                            }}
                                                        />
                                                        <div>
                                                            <Text strong>
                                                                Última transação
                                                                com cartão
                                                                físico com
                                                                sucesso
                                                            </Text>
                                                            <br />
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {renderDate(
                                                                    data?.lastSaleMachine,
                                                                )}
                                                            </Text>
                                                        </div>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        </Row>
                                    ),
                                },
                                {
                                    key: 'permissoes',
                                    label: (
                                        <span>
                                            <SafetyOutlined /> &nbsp; Permissões
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Permissões do Sistema"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        {[
                                                            {
                                                                label: 'Permissão de acesso aos arquivos?',
                                                                value: data?.has_filesystem_permission,
                                                            },
                                                            {
                                                                label: 'Permissão de acesso à localização?',
                                                                value: data?.has_location_permission,
                                                            },
                                                            {
                                                                label: 'Permissão de acesso ao telefone?',
                                                                value: data?.has_phone_permission,
                                                            },
                                                            {
                                                                label: 'Permissão de bluetooth?',
                                                                value: data?.has_bluetooth_permission,
                                                            },
                                                            {
                                                                label: 'Permissão de sobreposição?',
                                                                value: data?.has_overlay_permission,
                                                            },
                                                        ].map(
                                                            (
                                                                permission,
                                                                index,
                                                            ) => (
                                                                <Row
                                                                    key={index}
                                                                    justify="space-between"
                                                                    align="middle"
                                                                >
                                                                    <Text>
                                                                        {
                                                                            permission.label
                                                                        }
                                                                    </Text>
                                                                    <Space>
                                                                        {toBool(
                                                                            permission.value,
                                                                        ) ? (
                                                                            <CheckCircleOutlined
                                                                                style={{
                                                                                    color: '#52c41a',
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <CloseCircleOutlined
                                                                                style={{
                                                                                    color: '#ff4d4f',
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <Text>
                                                                            {toBool(
                                                                                permission.value,
                                                                            )
                                                                                ? 'Concedida'
                                                                                : 'Negada'}
                                                                        </Text>
                                                                    </Space>
                                                                </Row>
                                                            ),
                                                        )}
                                                    </Space>
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Status de Autenticação"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Token do
                                                                processador de
                                                                pagamentos
                                                                válido?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.plugpag_token_is_valid,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.plugpag_token_is_valid,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Autenticado com
                                                                a PagBank?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.authenticated,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.authenticated,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        {data?.plugpag_email && (
                                                            <Row
                                                                justify="space-between"
                                                                align="middle"
                                                            >
                                                                <Text>
                                                                    E-mail
                                                                    PagBank:
                                                                </Text>
                                                                <Space
                                                                    size={8}
                                                                    align="center"
                                                                >
                                                                    <Text
                                                                        type="secondary"
                                                                        ellipsis={{
                                                                            tooltip:
                                                                                data.plugpag_email,
                                                                        }}
                                                                        style={{
                                                                            maxWidth: 260,
                                                                            display:
                                                                                'inline-block',
                                                                        }}
                                                                    >
                                                                        {
                                                                            data.plugpag_email
                                                                        }
                                                                    </Text>
                                                                    <Tooltip title="Copiar e-mail">
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            aria-label="Copiar e-mail PagBank"
                                                                            icon={
                                                                                <CopyOutlined />
                                                                            }
                                                                            onClick={() => {
                                                                                handleCopyText(
                                                                                    data.plugpag_email!,
                                                                                );
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Space>
                                                            </Row>
                                                        )}
                                                        <Row
                                                            justify="space-between"
                                                            align="middle"
                                                        >
                                                            <Text>
                                                                Código de
                                                                ativação válido?
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.activation_code_is_valid,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.activation_code_is_valid,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        </Row>
                                    ),
                                },
                                {
                                    key: 'hardware',
                                    label: (
                                        <span>
                                            <MobileOutlined /> &nbsp; Hardware
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Informações do Dispositivo"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Modelo do
                                                                hardware:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.device_name,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Fabricante da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.device_manufacturer,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Marca da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.device_brand,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Modelo da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.device_product,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Placa da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.device_board,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Nível de
                                                                Bateria:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderPercent(
                                                                    data?.battery_level,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Leitor de Cartão e Identificação"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                ID da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.card_reader_serial,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Serial da
                                                                Maquininha:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.card_reader_mac ||
                                                                        selectedTotemData?.android_uid,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                UUID do Android:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    selectedTotemData?.android_uid,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Dispositivo
                                                                pareado e
                                                                disponível?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.paired,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.paired,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Conectado ao
                                                                leitor de código
                                                                de barras?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.is_connected_barcode_reader,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.is_connected_barcode_reader,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        </Row>
                                    ),
                                },
                                {
                                    key: 'sistema',
                                    label: (
                                        <span>
                                            <SettingOutlined /> &nbsp; Sistema
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Versões e Atualizações"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Versão do
                                                                Android:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.android_version,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Versão do Shell:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.version,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Versão do
                                                                Firmware:
                                                            </Text>
                                                            <Text
                                                                type="secondary"
                                                                ellipsis={{
                                                                    tooltip:
                                                                        data?.firmware_version,
                                                                }}
                                                            >
                                                                {renderText(
                                                                    data?.firmware_version?.slice(
                                                                        0,
                                                                        30,
                                                                    ),
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Data do último
                                                                registro de log:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderDate(
                                                                    data?.local_date,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Último
                                                                recarregamento
                                                                do app:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderDate(
                                                                    data?.reloaded_at,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Última
                                                                atualização do
                                                                app:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    undefined,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card
                                                    title="Status do Sistema"
                                                    size="small"
                                                    style={{
                                                        height: '100%',
                                                        minHeight: 320,
                                                    }}
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        style={{
                                                            width: '100%',
                                                            marginTop: -8,
                                                        }}
                                                        size={0}
                                                        split={
                                                            <Divider
                                                                style={{
                                                                    margin: '0',
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                App fixado em
                                                                tela?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.app_is_fixed,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.app_is_fixed,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Minimização de
                                                                app?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.has_kiosk_mode,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.has_kiosk_mode,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Teclado
                                                                virtual?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.has_virtual_keyboard,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.has_virtual_keyboard,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Memória RAM
                                                                total:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {formatMemoryMb(
                                                                    data?.memory_total,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Memória RAM
                                                                livre:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {formatMemoryMb(
                                                                    data?.memory_free,
                                                                )}
                                                                {typeof memoryUsagePercent ===
                                                                    'number' &&
                                                                    ` (${memoryUsagePercent}% em uso)`}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Conectado à
                                                                fonte de
                                                                energia?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.is_connected_power_source,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.is_connected_power_source,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Acesso ao
                                                                servidor
                                                                SHOPPBUD?:
                                                            </Text>
                                                            <Tag
                                                                color={getStatusColor(
                                                                    data?.is_accessing_shoppbud_server,
                                                                )}
                                                            >
                                                                {renderBool(
                                                                    data?.is_accessing_shoppbud_server,
                                                                )}
                                                            </Tag>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Tipo de
                                                                conexão?:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderText(
                                                                    data?.connection_type,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                        <Row justify="space-between">
                                                            <Text strong>
                                                                Último teste de
                                                                conexão com
                                                                internet com
                                                                sucesso:
                                                            </Text>
                                                            <Text type="secondary">
                                                                {renderDate(
                                                                    data?.last_successful_internet_connection,
                                                                )}
                                                            </Text>
                                                        </Row>
                                                    </Space>
                                                </Card>
                                            </Col>
                                        </Row>
                                    ),
                                },
                                ...(isSmartDevice
                                    ? [
                                          {
                                              key: 'modo-auxiliar',
                                              label: (
                                                  <span>
                                                      <ApiOutlined /> &nbsp;
                                                      Modo Auxiliar
                                                  </span>
                                              ),
                                              children: (
                                                  <Row gutter={[16, 16]}>
                                                      <Col xs={24} lg={12}>
                                                          <Card
                                                              title="Status do Modo Auxiliar"
                                                              size="small"
                                                              style={{
                                                                  height: '100%',
                                                                  minHeight: 320,
                                                              }}
                                                          >
                                                              <Space
                                                                  direction="vertical"
                                                                  style={
                                                                      compactSectionStyle
                                                                  }
                                                                  size={0}
                                                                  split={
                                                                      <Divider
                                                                          style={{
                                                                              margin: '0',
                                                                          }}
                                                                      />
                                                                  }
                                                              >
                                                                  <Row
                                                                      justify="space-between"
                                                                      align="middle"
                                                                  >
                                                                      <Text>
                                                                          Modo
                                                                          auxiliar
                                                                          habilitado?
                                                                      </Text>
                                                                      <Tag
                                                                          color={getStatusColor(
                                                                              data?.aux_mode_enabled,
                                                                          )}
                                                                      >
                                                                          {renderBool(
                                                                              data?.aux_mode_enabled,
                                                                          )}
                                                                      </Tag>
                                                                  </Row>
                                                                  <Row
                                                                      justify="space-between"
                                                                      align="middle"
                                                                  >
                                                                      <Text>
                                                                          Modo
                                                                          auxiliar
                                                                          pausado?
                                                                      </Text>
                                                                      <Tag
                                                                          color={
                                                                              toBool(
                                                                                  data?.aux_mode_paused,
                                                                              )
                                                                                  ? 'warning'
                                                                                  : 'success'
                                                                          }
                                                                      >
                                                                          {renderBool(
                                                                              data?.aux_mode_paused,
                                                                          )}
                                                                      </Tag>
                                                                  </Row>
                                                                  <Row
                                                                      justify="space-between"
                                                                      align="middle"
                                                                  >
                                                                      <Text>
                                                                          Conectado
                                                                          ao
                                                                          dispositivo
                                                                          auxiliar?
                                                                      </Text>
                                                                      <Tag
                                                                          color={getStatusColor(
                                                                              data?.aux_mode_connected,
                                                                          )}
                                                                      >
                                                                          {renderBool(
                                                                              data?.aux_mode_connected,
                                                                          )}
                                                                      </Tag>
                                                                  </Row>
                                                              </Space>
                                                          </Card>
                                                      </Col>

                                                      <Col xs={24} lg={12}>
                                                          <Card
                                                              title="Configuração do Dispositivo"
                                                              size="small"
                                                              style={{
                                                                  height: '100%',
                                                                  minHeight: 320,
                                                              }}
                                                          >
                                                              <Space
                                                                  direction="vertical"
                                                                  style={
                                                                      compactSectionStyle
                                                                  }
                                                                  size="large"
                                                              >
                                                                  <div>
                                                                      <Text
                                                                          strong
                                                                          style={{
                                                                              fontSize: 14,
                                                                          }}
                                                                      >
                                                                          Endereço
                                                                          do
                                                                          Dispositivo
                                                                          Auxiliar
                                                                      </Text>
                                                                      <br />
                                                                      <Text
                                                                          copyable={
                                                                              auxAddressMeta.value
                                                                                  ? {
                                                                                        text: auxAddressMeta.value,
                                                                                    }
                                                                                  : false
                                                                          }
                                                                          code={
                                                                              !!auxAddressMeta.value
                                                                          }
                                                                          style={{
                                                                              fontSize: 13,
                                                                              display:
                                                                                  'inline-block',
                                                                              marginTop: 8,
                                                                          }}
                                                                      >
                                                                          {renderText(
                                                                              auxAddressMeta.value,
                                                                          )}
                                                                      </Text>
                                                                      {auxAddressMeta.type && (
                                                                          <Tag
                                                                              color={
                                                                                  auxAddressMeta.type ===
                                                                                  'network'
                                                                                      ? 'geekblue'
                                                                                      : 'cyan'
                                                                              }
                                                                              style={{
                                                                                  marginLeft: 8,
                                                                              }}
                                                                          >
                                                                              {auxAddressMeta.type ===
                                                                              'network'
                                                                                  ? 'Rede'
                                                                                  : 'BT'}
                                                                          </Tag>
                                                                      )}
                                                                  </div>

                                                                  <Divider
                                                                      style={{
                                                                          margin: '12px 0',
                                                                      }}
                                                                  />

                                                                  <div>
                                                                      <Text
                                                                          type="secondary"
                                                                          style={{
                                                                              fontSize: 13,
                                                                          }}
                                                                      >
                                                                          <strong>
                                                                              Sobre
                                                                              o
                                                                              Modo
                                                                              Auxiliar:
                                                                          </strong>
                                                                          <br />
                                                                          Este
                                                                          dispositivo
                                                                          SMART
                                                                          está
                                                                          configurado
                                                                          como
                                                                          terminal
                                                                          auxiliar
                                                                          e fica
                                                                          disponível
                                                                          para
                                                                          que
                                                                          outro
                                                                          aplicativo
                                                                          se
                                                                          conecte
                                                                          a ele
                                                                          para o
                                                                          processamento
                                                                          de
                                                                          transações.
                                                                      </Text>
                                                                  </div>

                                                                  {toBool(
                                                                      data?.aux_mode_enabled,
                                                                  ) &&
                                                                      toBool(
                                                                          data?.aux_mode_connected,
                                                                      ) && (
                                                                          <div
                                                                              style={{
                                                                                  padding:
                                                                                      '12px',
                                                                                  background:
                                                                                      '#f6ffed',
                                                                                  border: '1px solid #b7eb8f',
                                                                                  borderRadius:
                                                                                      '4px',
                                                                              }}
                                                                          >
                                                                              <Space>
                                                                                  <CheckCircleOutlined
                                                                                      style={{
                                                                                          color: '#52c41a',
                                                                                      }}
                                                                                  />
                                                                                  <Text
                                                                                      style={{
                                                                                          color: '#389e0d',
                                                                                          fontSize: 13,
                                                                                      }}
                                                                                  >
                                                                                      Modo
                                                                                      auxiliar
                                                                                      ativo
                                                                                      e
                                                                                      operacional
                                                                                  </Text>
                                                                              </Space>
                                                                          </div>
                                                                      )}

                                                                  {toBool(
                                                                      data?.aux_mode_enabled,
                                                                  ) &&
                                                                      !toBool(
                                                                          data?.aux_mode_connected,
                                                                      ) && (
                                                                          <div
                                                                              style={{
                                                                                  padding:
                                                                                      '12px',
                                                                                  background:
                                                                                      '#fff7e6',
                                                                                  border: '1px solid #ffd591',
                                                                                  borderRadius:
                                                                                      '4px',
                                                                              }}
                                                                          >
                                                                              <Space>
                                                                                  <ExclamationCircleOutlined
                                                                                      style={{
                                                                                          color: '#faad14',
                                                                                      }}
                                                                                  />
                                                                                  <Text
                                                                                      style={{
                                                                                          color: '#d48806',
                                                                                          fontSize: 13,
                                                                                      }}
                                                                                  >
                                                                                      Modo
                                                                                      auxiliar
                                                                                      habilitado
                                                                                      mas
                                                                                      sem
                                                                                      conexão
                                                                                  </Text>
                                                                              </Space>
                                                                          </div>
                                                                      )}
                                                              </Space>
                                                          </Card>
                                                      </Col>
                                                  </Row>
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    key: 'controlar',
                                    label: (
                                        <span>
                                            <ControlOutlined /> &nbsp; Controlar
                                        </span>
                                    ),
                                    children: (
                                        <div>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: 'block',
                                                    marginBottom: 24,
                                                }}
                                            >
                                                Gerencie remotamente as
                                                funcionalidades do totem. Todas
                                                as ações requerem confirmação.
                                            </Text>
                                            <Row
                                                gutter={[16, 16]}
                                                align="stretch"
                                            >
                                                <Col xs={24} sm={12} lg={8}>
                                                    <Card
                                                        hoverable
                                                        style={{
                                                            height: '100%',
                                                            minHeight: 140,
                                                        }}
                                                        onClick={
                                                            handleRebootTerminal
                                                        }
                                                    >
                                                        <Space
                                                            direction="vertical"
                                                            align="center"
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <PoweroffOutlined
                                                                style={{
                                                                    fontSize: 32,
                                                                    color: '#ff4d4f',
                                                                }}
                                                            />
                                                            <Text strong>
                                                                Reiniciar
                                                                Terminal
                                                            </Text>
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 12,
                                                                    textAlign:
                                                                        'center',
                                                                }}
                                                            >
                                                                Reinicia
                                                                completamente o
                                                                dispositivo
                                                            </Text>
                                                        </Space>
                                                    </Card>
                                                </Col>

                                                {showAdminActions && (
                                                    <Col xs={24} sm={12} lg={8}>
                                                        <Card
                                                            hoverable
                                                            style={{
                                                                height: '100%',
                                                                minHeight: 140,
                                                            }}
                                                            onClick={
                                                                handleExportLogs
                                                            }
                                                        >
                                                            <Space
                                                                direction="vertical"
                                                                align="center"
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <DownloadOutlined
                                                                    style={{
                                                                        fontSize: 32,
                                                                        color: '#1890ff',
                                                                    }}
                                                                />
                                                                <Text strong>
                                                                    Baixar Logs
                                                                </Text>
                                                                <Text
                                                                    type="secondary"
                                                                    style={{
                                                                        fontSize: 12,
                                                                        textAlign:
                                                                            'center',
                                                                    }}
                                                                >
                                                                    Exporta logs
                                                                    do sistema
                                                                </Text>
                                                            </Space>
                                                        </Card>
                                                    </Col>
                                                )}

                                                {showAdminActions &&
                                                    !isSmartDevice && (
                                                        <Col
                                                            xs={24}
                                                            sm={12}
                                                            lg={8}
                                                        >
                                                            <Spin
                                                                spinning={
                                                                    isLoadingLogcat
                                                                }
                                                            >
                                                                <Card
                                                                    hoverable={
                                                                        !isLoadingLogcat
                                                                    }
                                                                    style={{
                                                                        height: '100%',
                                                                        minHeight: 140,
                                                                    }}
                                                                    onClick={() =>
                                                                        !isLoadingLogcat &&
                                                                        handleCollectLogcat()
                                                                    }
                                                                >
                                                                    <Space
                                                                        direction="vertical"
                                                                        align="center"
                                                                        style={{
                                                                            width: '100%',
                                                                        }}
                                                                    >
                                                                        <BugOutlined
                                                                            style={{
                                                                                fontSize: 32,
                                                                                color: '#722ed1',
                                                                            }}
                                                                        />
                                                                        <Text
                                                                            strong
                                                                        >
                                                                            Coletar
                                                                            Logcat
                                                                        </Text>
                                                                        <Text
                                                                            type="secondary"
                                                                            style={{
                                                                                fontSize: 12,
                                                                                textAlign:
                                                                                    'center',
                                                                            }}
                                                                        >
                                                                            Coleta
                                                                            logs
                                                                            de
                                                                            debug
                                                                            do
                                                                            Android
                                                                        </Text>
                                                                    </Space>
                                                                </Card>
                                                            </Spin>
                                                        </Col>
                                                    )}

                                                {isSmartDevice && (
                                                    <Col xs={24} sm={12} lg={8}>
                                                        <Card
                                                            style={{
                                                                height: '100%',
                                                                minHeight: 140,
                                                                backgroundColor:
                                                                    isKioskModeEnabled
                                                                        ? '#4b7cf308'
                                                                        : 'transparent',
                                                            }}
                                                            styles={{
                                                                body: {
                                                                    position:
                                                                        'relative',
                                                                    height: '100%',
                                                                    display:
                                                                        'flex',
                                                                    flexDirection:
                                                                        'column',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems:
                                                                        'center',
                                                                },
                                                            }}
                                                        >
                                                            <Tag
                                                                color={
                                                                    isKioskModeEnabled
                                                                        ? 'blue'
                                                                        : 'default'
                                                                }
                                                                style={{
                                                                    position:
                                                                        'absolute',
                                                                    top: 12,
                                                                    right: 12,
                                                                }}
                                                            >
                                                                {isKioskModeEnabled
                                                                    ? 'Ativo'
                                                                    : 'Inativo'}
                                                            </Tag>
                                                            <div
                                                                style={{
                                                                    position:
                                                                        'relative',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Space
                                                                    direction="vertical"
                                                                    align="center"
                                                                    style={{
                                                                        width: '100%',
                                                                        visibility:
                                                                            pendingToggle ===
                                                                            'kiosk'
                                                                                ? 'hidden'
                                                                                : 'visible',
                                                                        pointerEvents:
                                                                            pendingToggle ===
                                                                            'kiosk'
                                                                                ? 'none'
                                                                                : 'auto',
                                                                    }}
                                                                >
                                                                    <TabletOutlined
                                                                        style={{
                                                                            fontSize: 32,
                                                                            color: '#B2B0D0',
                                                                        }}
                                                                    />
                                                                    <Text
                                                                        strong
                                                                    >
                                                                        Bloquear
                                                                        Minimização
                                                                        do App
                                                                    </Text>
                                                                    <Text
                                                                        type="secondary"
                                                                        style={{
                                                                            fontSize: 12,
                                                                            textAlign:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        Impede
                                                                        que o
                                                                        app seja
                                                                        minimizado
                                                                        no
                                                                        dispositivo
                                                                    </Text>
                                                                    <Switch
                                                                        checked={
                                                                            isKioskModeEnabled
                                                                        }
                                                                        onChange={() =>
                                                                            setPendingToggle(
                                                                                'kiosk',
                                                                            )
                                                                        }
                                                                        aria-label="Bloquear minimização do app"
                                                                    />
                                                                </Space>
                                                                {pendingToggle ===
                                                                    'kiosk' && (
                                                                    <div
                                                                        style={{
                                                                            position:
                                                                                'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            bottom: 0,
                                                                            display:
                                                                                'flex',
                                                                            flexDirection:
                                                                                'column',
                                                                            justifyContent:
                                                                                'center',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            strong
                                                                        >
                                                                            {isKioskModeEnabled
                                                                                ? 'Desativar funcionalidade?'
                                                                                : 'Ativar funcionalidade?'}
                                                                        </Text>
                                                                        <Space>
                                                                            <Button
                                                                                type="primary"
                                                                                loading={
                                                                                    isLoadingSendCommand
                                                                                }
                                                                                onClick={
                                                                                    handleConfirmToggle
                                                                                }
                                                                            >
                                                                                Confirmar
                                                                            </Button>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    setPendingToggle(
                                                                                        null,
                                                                                    )
                                                                                }
                                                                            >
                                                                                Cancelar
                                                                            </Button>
                                                                        </Space>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Card>
                                                    </Col>
                                                )}

                                                {isSmartDevice && (
                                                    <Col xs={24} sm={12} lg={8}>
                                                        <Card
                                                            style={{
                                                                height: '100%',
                                                                minHeight: 140,
                                                                backgroundColor:
                                                                    isScannerAudioFeedbackEnabled
                                                                        ? '#4b7cf308'
                                                                        : 'transparent',
                                                            }}
                                                            styles={{
                                                                body: {
                                                                    position:
                                                                        'relative',
                                                                    height: '100%',
                                                                    display:
                                                                        'flex',
                                                                    flexDirection:
                                                                        'column',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems:
                                                                        'center',
                                                                },
                                                            }}
                                                        >
                                                            <Tag
                                                                color={
                                                                    isScannerAudioFeedbackEnabled
                                                                        ? 'blue'
                                                                        : 'default'
                                                                }
                                                                style={{
                                                                    position:
                                                                        'absolute',
                                                                    top: 12,
                                                                    right: 12,
                                                                }}
                                                            >
                                                                {isScannerAudioFeedbackEnabled
                                                                    ? 'Ativo'
                                                                    : 'Inativo'}
                                                            </Tag>
                                                            <div
                                                                style={{
                                                                    position:
                                                                        'relative',
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <Space
                                                                    direction="vertical"
                                                                    align="center"
                                                                    style={{
                                                                        width: '100%',
                                                                        visibility:
                                                                            pendingToggle ===
                                                                            'audio'
                                                                                ? 'hidden'
                                                                                : 'visible',
                                                                        pointerEvents:
                                                                            pendingToggle ===
                                                                            'audio'
                                                                                ? 'none'
                                                                                : 'auto',
                                                                    }}
                                                                >
                                                                    <SoundOutlined
                                                                        style={{
                                                                            fontSize: 32,
                                                                            color: '#52c41a',
                                                                        }}
                                                                    />
                                                                    <Text
                                                                        strong
                                                                    >
                                                                        Áudios
                                                                        de Erros
                                                                        do
                                                                        Leitor
                                                                    </Text>
                                                                    <Text
                                                                        type="secondary"
                                                                        style={{
                                                                            fontSize: 12,
                                                                            textAlign:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        Reproduz
                                                                        sons ao
                                                                        detectar
                                                                        erros no
                                                                        leitor
                                                                    </Text>
                                                                    <Switch
                                                                        checked={
                                                                            isScannerAudioFeedbackEnabled
                                                                        }
                                                                        onChange={() =>
                                                                            setPendingToggle(
                                                                                'audio',
                                                                            )
                                                                        }
                                                                        aria-label="Áudios de erros do leitor"
                                                                    />
                                                                </Space>
                                                                {pendingToggle ===
                                                                    'audio' && (
                                                                    <div
                                                                        style={{
                                                                            position:
                                                                                'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            bottom: 0,
                                                                            display:
                                                                                'flex',
                                                                            flexDirection:
                                                                                'column',
                                                                            justifyContent:
                                                                                'center',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            strong
                                                                        >
                                                                            {isScannerAudioFeedbackEnabled
                                                                                ? 'Desativar funcionalidade?'
                                                                                : 'Ativar funcionalidade?'}
                                                                        </Text>
                                                                        <Space>
                                                                            <Button
                                                                                type="primary"
                                                                                loading={
                                                                                    isLoadingSendCommand
                                                                                }
                                                                                onClick={
                                                                                    handleConfirmToggle
                                                                                }
                                                                            >
                                                                                Confirmar
                                                                            </Button>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    setPendingToggle(
                                                                                        null,
                                                                                    )
                                                                                }
                                                                            >
                                                                                Cancelar
                                                                            </Button>
                                                                        </Space>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Card>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </Card>
                </Space>
                {openModalExportLogs && (
                    <ExportLogsTotemModal
                        totemId={String(selectedTotem)}
                        onCancel={() => setOpenModalExportLogs(false)}
                    />
                )}

                <Modal
                    title="Logcat Coletado"
                    open={logcatModalOpen}
                    onCancel={handleCloseLogcatModal}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadLogcat}
                        >
                            Baixar
                        </Button>,
                        <Button key="close" onClick={handleCloseLogcatModal}>
                            Fechar
                        </Button>,
                    ]}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>
                            O logcat foi coletado com sucesso. Clique em
                            &quot;Baixar&quot; para obter o arquivo.
                        </Text>
                    </Space>
                </Modal>
            </div>
        </div>
    );
}
